import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import { 
  Wallet, 
  CreditCard, 
  DollarSign, 
  TrendingUp,
  RefreshCw,
  Plus,
  Settings
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';



const PaymentMethodIcon = ({ method }) => {
  const getIcon = () => {
    switch (method) {
      case 'credit_card':
        return <CreditCard className="h-4 w-4 text-blue-600" />;
      case 'paypal':
        return <div className="w-4 h-4 bg-blue-500 rounded text-white text-xs flex items-center justify-center font-bold">PP</div>;
      case 'apple_pay':
        return <div className="w-4 h-4 bg-black rounded text-white text-xs flex items-center justify-center font-bold">AP</div>;
      case 'google_pay':
        return <div className="w-4 h-4 bg-green-500 rounded text-white text-xs flex items-center justify-center font-bold">GP</div>;
      case 'bank_transfer':
        return <div className="w-4 h-4 bg-purple-500 rounded text-white text-xs flex items-center justify-center font-bold">BT</div>;
      case 'stripe':
        return <div className="w-4 h-4 bg-indigo-500 rounded text-white text-xs flex items-center justify-center font-bold">ST</div>;
      case 'square':
        return <div className="w-4 h-4 bg-gray-800 rounded text-white text-xs flex items-center justify-center font-bold">SQ</div>;
      case 'razorpay':
        return <div className="w-4 h-4 bg-blue-700 rounded text-white text-xs flex items-center justify-center font-bold">RP</div>;
      case 'paystack':
        return <div className="w-4 h-4 bg-teal-500 rounded text-white text-xs flex items-center justify-center font-bold">PS</div>;
      default:
        return <Wallet className="h-4 w-4 text-gray-600" />;
    }
  };

  return getIcon();
};

const PaymentMethodsTable = () => {
  const { token, selectedPlatform , backendUrl} = useAuth();
  
  // Dynamic API URL for dev/prod environments
  const API = `${backendUrl}/api`;
  const { toast } = useToast();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [processors, setProcessors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPaymentMethodData = async () => {
    if (!token || !selectedPlatform) return;
    
    try {
      setLoading(true);
      
      // Fetch dashboard analytics to get payment method and processor stats
      const response = await axios.get(`${API}/analytics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          platform_id: selectedPlatform,
          days: 30 // Last 30 days
        }
      });
      
      const data = response.data;
      setPaymentMethods(data.payment_methods || []);
      setProcessors(data.processors || []);
      setError(null);
      
    } catch (err) {
      console.error('Error fetching payment method data:', err);
      setError('Failed to load payment method data');
      toast({
        title: "Error",
        description: "Failed to load payment method data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethodData();
  }, [token, selectedPlatform]);

  const handleRefresh = () => {
    fetchPaymentMethodData();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatMethodName = (method) => {
    return method.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading && paymentMethods.length === 0) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Payment Methods</h1>
            <p className="text-muted-foreground mt-2">Loading payment method data...</p>
          </div>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-200 rounded"></div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Payment Methods</h1>
          <p className="text-muted-foreground mt-2">
            Manage and view payment method statistics
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button onClick={handleRefresh} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Method
          </Button>
          <Button variant="outline" size="sm">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      {/* Payment Methods Statistics Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Methods Statistics (Last 30 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={handleRefresh} variant="outline">
                Try Again
              </Button>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Payment Method</TableHead>
                    <TableHead>Transactions</TableHead>
                    <TableHead>Percentage</TableHead>
                    <TableHead>Total Volume</TableHead>
                    <TableHead>Avg Transaction</TableHead>
                    <TableHead>Primary Processor</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paymentMethods.map((method, index) => (
                    <TableRow key={index} className="hover:bg-muted/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <PaymentMethodIcon method={method.method} />
                          <div>
                            <div className="font-medium">
                              {formatMethodName(method.method)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {method.method}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{method.count.toLocaleString()}</span>
                          <TrendingUp className="h-3 w-3 text-green-500" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="font-mono">
                          {method.percentage}%
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold text-green-600">
                          {formatCurrency(method.total_amount)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {formatCurrency(method.total_amount / method.count)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {formatMethodName(method.processor)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                          Active
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Processors Statistics Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Payment Processors Statistics (Last 30 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Processor</TableHead>
                  <TableHead>Transactions</TableHead>
                  <TableHead>Market Share</TableHead>
                  <TableHead>Total Volume</TableHead>
                  <TableHead>Success Rate</TableHead>
                  <TableHead>Avg Processing Fee</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {processors.map((processor, index) => (
                  <TableRow key={index} className="hover:bg-muted/50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <PaymentMethodIcon method={processor.processor} />
                        <div>
                          <div className="font-medium">
                            {formatMethodName(processor.processor)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Payment Processor
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{processor.count.toLocaleString()}</span>
                        <TrendingUp className="h-3 w-3 text-green-500" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-mono">
                        {processor.percentage}%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="font-semibold text-green-600">
                        {formatCurrency(processor.total_amount)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant={processor.success_rate >= 95 ? "default" : processor.success_rate >= 90 ? "secondary" : "destructive"}
                          className={processor.success_rate >= 95 ? "bg-green-100 text-green-800 border-green-200" : ""}
                        >
                          {processor.success_rate}%
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-muted-foreground">
                        ~2.9% + $0.30
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                        Active
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Methods</p>
                <p className="text-2xl font-bold">{paymentMethods.length}</p>
              </div>
              <CreditCard className="h-6 w-6 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Processors</p>
                <p className="text-2xl font-bold">{processors.length}</p>
              </div>
              <Wallet className="h-6 w-6 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Volume</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(paymentMethods.reduce((sum, method) => sum + method.total_amount, 0))}
                </p>
              </div>
              <DollarSign className="h-6 w-6 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Success Rate</p>
                <p className="text-2xl font-bold">
                  {processors.length > 0 
                    ? Math.round(processors.reduce((sum, proc) => sum + proc.success_rate, 0) / processors.length)
                    : 0}%
                </p>
              </div>
              <TrendingUp className="h-6 w-6 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentMethodsTable;